import React, { useCallback, useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Modal } from 'antd';
import * as R from 'ramda';

import PolicyAuditContext from '@totem/components/policyAudits/policyAuditDetail/PolicyAuditContext';
import UserProfileContext from '@totem/components/userContext/UserProfileContext';
import { PolicyAuditErrors } from '@totem/types/policyAudit';
import authUtilities from '@totem/utilities/authUtilities';
import { V2_SURVEY_ENDPOINT } from '@totem/utilities/endpoints';
import { validatePolicyAudit } from '@totem/utilities/policyAuditUtilities';
import { CheckResponseShowError } from '@totem/utilities/responseUtilities';
interface PassedProps {
  onError: (errors: PolicyAuditErrors) => void;
  hideReopen?: boolean;
}
type Props = PassedProps;
interface State {
  loading: boolean;
  visible: boolean;
  submitted: boolean;
  patched: boolean;
}
const initialState: State = {
  loading: false,
  visible: false,
  submitted: false,
  patched: false,
};
const SubmitButton = ({ onError, hideReopen }: Props) => {
  const { userProfile, getApiToken } = useContext(UserProfileContext);
  const navigate = useNavigate();
  const [sending, setSending] = useState<boolean>(false);
  const [state, setState] = useState<State>(initialState);
  const { data, loading, refetch } = useContext(PolicyAuditContext);
  const policyAudit = data;
  useEffect(() => {
    if (loading && !state.loading) {
      setState((prevState) => ({ ...prevState, loading: true }));
      return;
    }
    if (!loading && state.loading) {
      if (!state.patched) {
        setState((prevState) => ({ ...prevState, loading: false }));
        return;
      }
      const basePath = authUtilities.isVendorUser(userProfile)
        ? 'vendor-dashboard'
        : 'dashboard';
      if (state.submitted) {
        navigate(`/${basePath}/policyaudits/${policyAudit.id}/report`);
      }
      setState({ ...initialState });
    }
  }, [loading, state.loading, state.submitted]);
  const sendAuditSubmittedUpdate = useCallback(
    async (id: string, submitted: boolean) => {
      if (!sending) {
        fetch(
          `${V2_SURVEY_ENDPOINT}/policyAudit/legacy/${id}/submitted/${submitted}`,
          {
            method: 'PATCH',
            headers: new Headers({
              Authorization: `Bearer ${getApiToken()}`,
            }),
          },
        ).then((res) => {
          setSending(false);

          setState((prevState) => ({ ...prevState, visible: false }));
          const success = CheckResponseShowError(res);
          if (success) {
            refetch();
          }
        });
      }
    },
    [],
  );
  useEffect(() => {
    if (state.patched) {
      sendAuditSubmittedUpdate(policyAudit.id, state.submitted);
    }
  }, [state.patched, state.submitted, getApiToken]);
  const toggleModal = () => {
    setState((prevState) => ({ ...prevState, visible: !state.visible }));
  };
  const handleSubmitClick = () => {
    const { submitted } = policyAudit;
    if (submitted) {
      setState((prevState) => ({ ...prevState, visible: true }));
      return;
    }
    const errors = validatePolicyAudit(policyAudit);
    if (R.isEmpty(errors)) {
      setState((prevState) => ({ ...prevState, visible: true }));
    } else {
      onError(errors);
    }
  };
  const handleSubmitToggle = () => {
    const { submitted } = policyAudit;
    setState((prevState) => ({
      ...prevState,
      patched: true,
      submitted: !submitted,
    }));
  };
  const showButton = hideReopen ? !policyAudit.submitted : true;
  return (
    <>
      {showButton && (
        <Button type="primary" onClick={handleSubmitClick}>
          {policyAudit.submitted ? 'Reopen Audit' : 'Submit Audit'}
        </Button>
      )}
      <Modal
        open={state.visible}
        confirmLoading={state.loading}
        onOk={handleSubmitToggle}
        onCancel={toggleModal}
        title="Confirm"
        okText="Submit"
      >
        {policyAudit.submitted
          ? 'Are you sure you want to reopen this audit?'
          : 'Are you sure you want to submit this audit for scoring?'}
      </Modal>
    </>
  );
};
export default SubmitButton;
